import React, { useRef, useState, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { useLabels, useRequestData, useFlags } from 'wsm-common-data';
import { setClassNames } from '@ddc/classnames-js';
import { useSelector } from 'react-redux';
import { galleryTrackingEvents } from '../../utilities/tracking';
import { GALLERY_TABS } from '../../constants/galleryTabs';
import { useGalleryContext } from '../../contexts/GalleryContext';

import ImageSlider from '../common/ImageSlider';
import GalleryButton from './GalleryButton';
import useHorizontalLayout from '../../hooks/useHorizontalLayout';
import Thumbnails from '../thumbnails/Thumbnails';
import DDCPlayerVideoComponent from '../DDCPlayerVideo/DDCPlayerVideoComponent';
import VehicleImageCount from '../common/VehicleImageCount';

const GalleryModal = () => {
	const { state, actions, meta } = useGalleryContext();
	const { openModal, carouselIndex, activeTab, imagesToDisplay } = state;
	const { setCarouselIndex, setActiveTab, onClose } = actions;
	const { isDesktop, imgTitle, isBlackBackgroundModal } = meta;

	const { videos = [] } = useSelector((storeState) => storeState.media);
	const initialFocusRef = useRef(null);
	const modalSliderRef = useRef(null);
	const [modalWrapperElement, setModalWrapperElement] = useState(null);
	const modalWrapperRef = useCallback((node) => {
		if (node !== null) {
			setModalWrapperElement(node);
		}
	}, []);
	const labels = useLabels();
	const { widgetName, windowId } = useRequestData();
	const isHorizontalLayout = useHorizontalLayout();
	const flags = useFlags();
	const flagHorizontalTabs =
		flags['ws-vehicle-gallery-toggle-horizontal-modal-tabs'] || false;
	const isHorizontalTabs = isHorizontalLayout && flagHorizontalTabs;

	// Determine modal layout class — extracted to avoid nested ternary
	let modalLayoutClass = 'layout-vertical';
	if (isHorizontalTabs) {
		modalLayoutClass = 'layout-horizontal-tabs';
	} else if (isHorizontalLayout) {
		modalLayoutClass = 'layout-horizontal';
	}

	const isMobile = !isDesktop;
	const { widgetInfo } = useSelector((storeState) => storeState);
	const hasVideo = videos.length > 0;
	const TOTAL_IMAGES = imagesToDisplay.length;

	const btnSpace = 'mx-0';
	const supportARIA = (modal) => {
		const modalWrap = modal.closest('[aria-hidden]');
		if (modalWrap) {
			modalWrap.removeAttribute('aria-hidden');
		}
	};

	const handleOpenModal = () => {
		const modal = document.querySelector('#ws-vehicle-gallery-modal');
		if (modal) {
			supportARIA(modal);
			initialFocusRef?.current?.focus();
			const slickArrows = document.querySelectorAll(
				'.slick-arrow.slick-next, slick-arrow.slick-prev'
			);
			slickArrows.forEach((arrow) => {
				arrow.setAttribute('tabindex', '-1');
				arrow.setAttribute('aria-hidden', 'true');
			});
		}
		// For the ws-vehicle-toolbar portal inside the modal,
		// enable the mobile slidein for the toolbar CTA
		if (window?.DDC?.client?.isMobile) {
			window.DDC.mobile?.slidein?.init(
				'.vehicle-gallery-toolbar-react-portal .dialog'
			);
		}
	};

	return (
		<Modal
			show={openModal}
			onHide={onClose}
			onEntered={handleOpenModal}
			backdrop="static"
			id="ws-vehicle-gallery-modal"
			className={setClassNames([
				isMobile ? 'isMobile w-100 border-0' : '',
				isBlackBackgroundModal ? 'black-bg-modal' : ''
			])}
			aria-modal="true"
			aria-label={labels.get('IMAGE_GALLERY')}
		>
			<div className="border-bottom-0 text-body">
				<div className="border-bottom">
					<div className="container-max-md px-sm-4 px-lg-8 py-3">
						<div className="vehicle-gallery-toolbar-react-portal">
							<button
								type="button"
								aria-label={labels.get('CLOSE')}
								className="float-right btn-unstyled text-link-muted mt-5 ml-3 p-0 border-bottom-0"
								onClick={onClose}
								ref={initialFocusRef}
							>
								<i
									className="ddc-icon ddc-icon-remove2"
									aria-hidden="true"
								/>
							</button>
						</div>
					</div>
				</div>
				<div
					className={`mt-5 mt-lg-6 ${
						!isMobile ? 'container-max-md px-lg-6 px-xl-8' : ''
					} ${!isHorizontalLayout ? 'mt-xl-8' : ''}`}
				>
					<div
						data-testid="modal-content-gallery"
						className={`modal-content-gallery ${modalLayoutClass}`}
					>
						<div className="modal-content-gallery-menu mb-3 mb-lg-0 pl-4 pl-md-2 pl-lg-4 pl-xl-0">
							{hasVideo && (
								<div
									data-testid="tab-container"
									className={`d-flex ${
										isHorizontalTabs
											? 'flex-row mb-4'
											: 'flex-row flex-md-column'
									}`}
								>
									<div
										className={
											isHorizontalTabs
												? 'mr-5'
												: 'mb-md-5 mr-5 mr-md-0'
										}
									>
										<GalleryButton
											variant="modal-tab"
											icon="ddc-icon-images2"
											btnLabel={labels.get('PHOTOS')}
											btnSpace={btnSpace}
											activeToolBar={activeTab}
											value={GALLERY_TABS.PHOTOS}
											handleClick={() => {
												galleryTrackingEvents.clickPhotosTab(
													widgetName,
													windowId
												);
												setActiveTab(
													GALLERY_TABS.PHOTOS
												);
											}}
										/>
									</div>
									<div>
										<GalleryButton
											variant="modal-tab"
											icon="ddc-icon-play3"
											btnLabel={labels.get('VIDEO')}
											btnSpace={btnSpace}
											activeToolBar={activeTab}
											value={GALLERY_TABS.VIDEO}
											handleClick={() => {
												galleryTrackingEvents.clickVideoTab(
													widgetName,
													windowId
												);
												setActiveTab(
													GALLERY_TABS.VIDEO
												);
											}}
										/>
									</div>
								</div>
							)}
						</div>
						<div
							className="modal-content-gallery-slider"
							ref={modalWrapperRef}
						>
							{activeTab === GALLERY_TABS.VIDEO ? (
								<DDCPlayerVideoComponent
									video={videos[0]}
									widgetInfo={widgetInfo}
								/>
							) : (
								<ImageSlider
									sliderRef={modalSliderRef}
									widgetElement={modalWrapperElement}
								/>
							)}
						</div>

						{activeTab === GALLERY_TABS.PHOTOS ? (
							<div
								className="modal-content-gallery-thumbnails"
								id="gallery-modal-thumbnail-wrapper"
							>
								{isMobile ? (
									<div className="mb-3 mt-n3">
										<VehicleImageCount
											className="btn btn-unstyled pointer-events-none translate-left-50 vehicle-image-count mb-3 mt-n3"
											openModal={openModal}
											mediaCount={TOTAL_IMAGES}
											imageIndex={carouselIndex + 1}
										/>
									</div>
								) : (
									''
								)}
								<Thumbnails
									carouselIndex={carouselIndex}
									setCarouselIndex={setCarouselIndex}
									isMobile={isMobile}
									key={isMobile}
									modalSliderRef={modalSliderRef}
									imgTitle={imgTitle}
									isHorizontalLayout={isHorizontalLayout}
								/>
							</div>
						) : (
							<></>
						)}
					</div>
				</div>
			</div>
		</Modal>
	);
};

GalleryModal.propTypes = {};

export default GalleryModal;
